/**
 * DeviceStockMovementCard — "ความเคลื่อนไหวสต็อก" ในโมดัลรายละเอียดอุปกรณ์
 *
 * ตอบคำถามที่ผู้ใช้ถามหน้าเครื่องบ่อยที่สุด: "ยอดนี้มาได้ยังไง / ของหายไปไหน"
 * โดยไม่ต้องออกจากโมดัลไปหน้าสมุดบัญชีคลัง
 *
 * backend: GET /devices/{id}/stock-movements (perm `stock_movements.view`)
 * — เปิดดูได้แม้อุปกรณ์ถูก soft-delete ไปแล้ว
 * ไม่มีสิทธิ์ = ไม่ render และไม่ยิง API (pattern G-27 กัน 403 เงียบ ๆ)
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  RiExchangeBoxLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiExternalLinkLine,
  RiLoader4Line,
  RiErrorWarningLine,
} from 'react-icons/ri';
import stockMovementService, { StockMovementRow } from '../../services/stockMovementService';
import { usePermission } from '../../hooks/usePermission';
import { formatDateTimeFull } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

/** จำนวนแถวล่าสุดที่แสดงในโมดัล — ดูครบให้ไปหน้าความเคลื่อนไหวสต็อก */
const PREVIEW_SIZE = 8;

interface DeviceStockMovementCardProps {
  deviceId: number;
  className?: string;
}

const DeviceStockMovementCard: React.FC<DeviceStockMovementCardProps> = ({
  deviceId,
  className = '',
}) => {
  const { hasPermission, isSuperAdmin } = usePermission();
  const canView = isSuperAdmin || hasPermission('stock_movements.view');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['stock-movements', 'device-card', deviceId],
    queryFn: () => stockMovementService.getDeviceMovements(deviceId, { page_size: PREVIEW_SIZE }),
    enabled: canView && !!deviceId,
    retry: false,
  });

  if (!canView) return null;

  const items = data?.items || [];
  const total = data?.total || 0;

  const renderShell = (children: React.ReactNode) => (
    <div className={`mt-4 p-4 border border-secondary-200 rounded-lg bg-white ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-secondary-200 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
            <RiExchangeBoxLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ความเคลื่อนไหวสต็อก</h3>
          {total > 0 && (
            <span className="bg-secondary-100 text-secondary-600 text-xs px-2 py-0.5 rounded-full">
              {total}
            </span>
          )}
        </div>
        {total > 0 && (
          <Link
            to={`/stock/movements?device_id=${deviceId}`}
            className="text-xs text-primary-600 hover:underline inline-flex items-center"
          >
            ดูทั้งหมด
            <RiExternalLinkLine className="w-3.5 h-3.5 ml-1" />
          </Link>
        )}
      </div>
      {children}
    </div>
  );

  if (isLoading) {
    return renderShell(
      <div className="flex items-center justify-center py-6 text-secondary-500">
        <RiLoader4Line className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">กำลังโหลดความเคลื่อนไหวสต็อก...</span>
      </div>
    );
  }

  if (error) {
    return renderShell(
      <div className="flex flex-col items-center gap-3 py-6">
        <div className="flex items-center text-danger-600 text-sm">
          <RiErrorWarningLine className="w-5 h-5 mr-2" />
          {getApiErrorMessage(error, 'ไม่สามารถโหลดความเคลื่อนไหวสต็อกของเครื่องนี้ได้')}
        </div>
        <button type="button" className="btn btn-secondary px-3 py-1.5 text-sm" onClick={() => refetch()}>
          ลองใหม่
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return renderShell(
      <div className="text-center py-6 text-secondary-500">
        <RiExchangeBoxLine className="w-10 h-10 mx-auto text-secondary-300 mb-2" />
        <p className="text-sm">ยังไม่มีความเคลื่อนไหวของอุปกรณ์ชิ้นนี้</p>
        <p className="text-xs text-secondary-400 mt-1">
          ระบบเริ่มบันทึกสมุดบัญชีสต็อกตั้งแต่ 7 ก.ย. 2026 — รายการก่อนหน้านั้นไม่มีข้อมูล
        </p>
      </div>
    );
  }

  const renderDelta = (mv: StockMovementRow) => {
    if (mv.direction === 'STATUS_CHANGE' || mv.quantity === 0) return null;
    const Icon = mv.direction === 'IN' ? RiArrowUpLine : mv.direction === 'OUT' ? RiArrowDownLine : null;
    const tone =
      mv.direction === 'IN'
        ? 'text-success-700'
        : mv.direction === 'OUT'
        ? 'text-danger-700'
        : 'text-secondary-700';
    return (
      <span className={`inline-flex items-center text-sm font-medium ${tone}`}>
        {Icon && <Icon className="w-3.5 h-3.5 mr-0.5" />}
        {mv.quantity}
      </span>
    );
  };

  return renderShell(
    <div className="space-y-2">
      {items.map((mv) => (
        <div key={mv.id} className="border border-secondary-200 rounded-md p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm text-secondary-900">{mv.reason_label}</p>
              <p className="text-xs text-secondary-500">
                {mv.created_at ? formatDateTimeFull(mv.created_at) : '-'} ·{' '}
                {/* NULL = ระบบทำเอง (cron/migration) ไม่ใช่คน */}
                {mv.performed_by_name || 'ระบบ'}
                {mv.ref_type_label ? ` · ${mv.ref_type_label}${mv.ref_id ? ` #${mv.ref_id}` : ''}` : ''}
              </p>
            </div>
            <div className="text-right">
              {renderDelta(mv)}
              <p className="text-xs text-secondary-500">
                {mv.balance_before !== null && mv.balance_after !== null
                  ? `${mv.balance_before} → ${mv.balance_after}`
                  : mv.status_before || mv.status_after
                  ? `${mv.status_before_label || mv.status_before || '-'} → ${mv.status_after_label || mv.status_after || '-'}`
                  : ''}
              </p>
            </div>
          </div>
          {mv.note && <p className="mt-1 text-xs text-secondary-500 italic">{mv.note}</p>}
        </div>
      ))}
    </div>
  );
};

export default DeviceStockMovementCard;
